import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "../../Authservices/axiosInstance";
import {
  FaMoneyBillWave,
  FaUsers,
  FaUmbrellaBeach,
  FaHeadSideMask,
  FaRegClock,
  FaSignInAlt,
  FaCoffee,
  FaSignOutAlt,
  FaPlusCircle,
  FaUserTie,
  FaTimes,
  FaUserSlash,
} from "react-icons/fa";
import { MdAccessTimeFilled, MdOutlineAddTask } from "react-icons/md";
import { IoMdTime } from "react-icons/io";
import { LuLayoutDashboard } from "react-icons/lu";
import "./dashboard.css";

const EmployeeDashboard = () => {
  const [dashboardData, setDashboardData] = useState({
    totalSalary: 0,
    totalEmployees: 0,
    totalHR: 0,
    totalActiveUsers: 0,
    checkedInCount: 0,
    checkedOutCount: 0,
    onBreakCount: 0,
    holidayCount: 0,
    sicknessCount: 0,
    lateCount: 0,
    toilCount: 0,
    additionalCount: 0
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showLateModal, setShowLateModal] = useState(false);
  const [lateEmployees, setLateEmployees] = useState([]);
  const [loadingLate, setLoadingLate] = useState(false);
  const [showAbsentModal, setShowAbsentModal] = useState(false);
  const [absentEmployees, setAbsentEmployees] = useState([]);
  const [loadingAbsent, setLoadingAbsent] = useState(false);
  const [absentCount, setAbsentCount] = useState(0);

  const formatSalary = (amount) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Fetch main dashboard metrics
  const fetchDashboard = async () => {
    try {
      const response = await axios.get(
        "https://backend.ems.uk.hiradentallab.com/api/attendance/dashboard-metrics"
      );
      if (response.data.success) {
        setDashboardData(response.data.data);
        setLastUpdated(new Date().toLocaleTimeString());
      }
    } catch (err) {
      console.error("Dashboard error:", err);
      setError("Failed to load dashboard data");
    }
  };

  // Fetch absent list and count (without opening modal)
  const fetchAbsentData = async () => {
    try {
      const today = new Date();
      const formattedDate = formatDateForAPI(today);
      const response = await axios.get(
        `https://backend.ems.uk.hiradentallab.com/api/attendance/absent-users/${formattedDate}`
      );
      if (response.data.success) {
        const absents = response.data.absents.map(item => ({
          userId: item.userId,
          name: item.name,
          department: item.department || 'Not specified',
          deductionMultiplier: item.deductionMultiplier || 1
        }));
        setAbsentEmployees(absents);
        setAbsentCount(absents.length);
      } else {
        setAbsentEmployees([]);
        setAbsentCount(0);
      }
    } catch (err) {
      console.error("Error fetching absent data:", err);
      setAbsentEmployees([]);
      setAbsentCount(0);
    }
  };

  // Fetch late employees list (only on click)
  const fetchLateEmployees = async () => {
    setLoadingLate(true);
    try {
      const response = await axios.get(
        "https://backend.ems.uk.hiradentallab.com/api/attendance/late-employees"
      );
      if (response.data.success) {
        setLateEmployees(response.data.data);
        setShowLateModal(true);
      }
    } catch (err) {
      console.error("Error fetching late employees:", err);
      alert("Could not load late employees list.");
    } finally {
      setLoadingLate(false);
    }
  };

  // Open absent modal (data already loaded)
  const handleShowAbsentModal = () => {
    setShowAbsentModal(true);
  };

  // Initial load and auto-refresh
  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchAbsentData()]);
      setLoading(false);
    };
    loadAllData();

    const interval = setInterval(async () => {
      await Promise.all([fetchDashboard(), fetchAbsentData()]);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return <div className="dashboard-loading">Loading dashboard...</div>;
  }

  const {
    totalSalary,
    totalEmployees,
    totalHR,
    totalActiveUsers,
    checkedInCount,
    checkedOutCount,
    onBreakCount,
    holidayCount,
    sicknessCount,
    lateCount,
    toilCount,
    additionalCount
  } = dashboardData;

  return (
    <div className="dashboard-wrapper">
      <div className="dashboard">
        {/* Header */}
        <div className="header">
          <h1 className="dashboard-title">
            <LuLayoutDashboard /> Dashboard
          </h1>
          <div className="header-links">
            <Link to="/" className="header-link">
              Not Checked-In <MdAccessTimeFilled />
            </Link>
            <button className="header-button">
              10:00 pm <IoMdTime />
            </button>
            <button className="header-button">
              00:00 <IoMdTime />
            </button>
          </div>
          <hr />
        </div>

        {lastUpdated && <div className="last-updated">Last updated: {lastUpdated}</div>}

        {/* SECTION 1: Payroll & Employees */}
        <div className="dashboard-section">
          <h2 className="section-title"><FaMoneyBillWave /> Payroll & Employees</h2>
          <div className="dashboard-grid">
            <div className="stat-card stat-card-salary">
              <div className="stat-card-header"><FaMoneyBillWave className="stat-icon" /><span>Total Salaries</span></div>
              <div className="stat-card-value">{formatSalary(totalSalary)}</div>
              <div className="stat-card-label">Monthly payroll</div>
              <Link to="/basic-salary" className="stat-card-link">View Details →</Link>
            </div>
            <div className="stat-card">
              <div className="stat-card-header"><FaUsers className="stat-icon" /><span>Total Employees</span></div>
              <div className="stat-card-value">{totalEmployees}</div>
              <div className="stat-card-label">All company employees</div>
              <Link to="/emp-info" className="stat-card-link">See Employees →</Link>
            </div>
            <div className="stat-card">
              <div className="stat-card-header"><FaUserTie className="stat-icon" /><span>HR Staff</span></div>
              <div className="stat-card-value">{totalHR}</div>
              <div className="stat-card-label">Human resources team</div>
              <Link to="/hr-info" className="stat-card-link">See HR →</Link>
            </div>
            <div className="stat-card">
              <div className="stat-card-header"><FaMoneyBillWave className="stat-icon" /><span>Salary by Dept</span></div>
              <div className="stat-card-value">5 Depts</div>
              <div className="stat-card-label">Department-wise salary</div>
              <Link to="/dept-salary" className="stat-card-link">View Salary →</Link>
            </div>
          </div>
        </div>

        {/* SECTION 2: Attendance */}
        <div className="dashboard-section">
          <h2 className="section-title"><FaRegClock /> Attendance</h2>
          <div className="dashboard-grid">
            <div className="stat-card stat-card-primary">
              <div className="stat-card-header"><FaSignInAlt className="stat-icon" /><span>Clocked In</span></div>
              <div className="stat-card-value">{checkedInCount} / {totalActiveUsers}</div>
              <div className="stat-card-label">Working today</div>
              <Link to="/current-atd" className="stat-card-link">View Attendance →</Link>
            </div>
            <div className="stat-card">
              <div className="stat-card-header"><FaCoffee className="stat-icon" /><span>On Break</span></div>
              <div className="stat-card-value">{onBreakCount} / {totalActiveUsers}</div>
              <div className="stat-card-label">Currently on break</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-header"><FaSignOutAlt className="stat-icon" /><span>Clocked Out</span></div>
              <div className="stat-card-value">{checkedOutCount} / {totalActiveUsers}</div>
              <div className="stat-card-label">Finished work today</div>
            </div>
            <div className="stat-card" onClick={fetchLateEmployees} style={{ cursor: "pointer" }}>
              <div className="stat-card-header"><FaRegClock className="stat-icon" /><span>Who's Late</span></div>
              <div className="stat-card-value">{lateCount} / {totalActiveUsers}</div>
              <div className="stat-card-label">Arrived after start time</div>
            </div>
            {/* Absent Card - count is fetched automatically, click opens modal */}
            <div className="stat-card" onClick={handleShowAbsentModal} style={{ cursor: "pointer" }}>
              <div className="stat-card-header"><FaUserSlash className="stat-icon" /><span>Absent</span></div>
              <div className="stat-card-value">{absentCount} / {totalActiveUsers}</div>
              <div className="stat-card-label">No check‑in, not on leave</div>
            </div>
          </div>
        </div>

        {/* SECTION 3: Leaves */}
        <div className="dashboard-section">
          <h2 className="section-title"><FaUmbrellaBeach /> Leaves</h2>
          <div className="dashboard-grid">
            <div className="stat-card">
              <div className="stat-card-header"><FaUmbrellaBeach className="stat-icon" /><span>Holiday</span></div>
              <div className="stat-card-value">{holidayCount} / {totalActiveUsers}</div>
              <div className="stat-card-label">On leave today</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-header"><FaHeadSideMask className="stat-icon" /><span>Sickness</span></div>
              <div className="stat-card-value">{sicknessCount} / {totalActiveUsers}</div>
              <div className="stat-card-label">On sick leave</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-header"><MdOutlineAddTask className="stat-icon" /><span>TOIL</span></div>
              <div className="stat-card-value">{toilCount} / {totalActiveUsers}</div>
              <div className="stat-card-label">Time off in lieu</div>
            </div>
            <div className="stat-card">
              <div className="stat-card-header"><FaPlusCircle className="stat-icon" /><span>Additional</span></div>
              <div className="stat-card-value">{additionalCount} / {totalActiveUsers}</div>
              <div className="stat-card-label">Extra hours / overtime</div>
            </div>
          </div>
        </div>

        <div className="dashboard-footer">
          <div className="footer-stats">
            <span>Total Employees: {totalEmployees}</span>
            <span>HR Staff: {totalHR}</span>
            <span>Active Today: {checkedInCount}</span>
          </div>
        </div>

        {error && <div className="dashboard-error">{error}</div>}
      </div>

      {/* Late Modal */}
      {showLateModal && (
        <div className="modal-overlay" onClick={() => setShowLateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h3>Late Employees Today</h3><button className="modal-close" onClick={() => setShowLateModal(false)}><FaTimes /></button></div>
            <div className="modal-body">
              {loadingLate ? <p>Loading...</p> : lateEmployees.length === 0 ? <p>No late arrivals today 👍</p> : (
                <table className="late-table"><thead><tr><th>Name</th><th>Late (min)</th><th>Check‑In</th><th>Shift Start</th></tr></thead>
                <tbody>{lateEmployees.map(emp => (<tr key={emp.userId}><td>{emp.name}</td><td className="late-minutes">{emp.lateMinutes}</td><td>{emp.checkIn}</td><td>{emp.shiftStart}</td></tr>))}</tbody></table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Absent Modal */}
      {showAbsentModal && (
        <div className="modal-overlay" onClick={() => setShowAbsentModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h3>Absent Employees Today</h3><button className="modal-close" onClick={() => setShowAbsentModal(false)}><FaTimes /></button></div>
            <div className="modal-body">
              {absentEmployees.length === 0 ? <p>Everyone is present today! 🎉</p> : (
                <table className="late-table"><thead><tr><th>Name</th><th>Department</th><th>Deduction</th></tr></thead>
                <tbody>{absentEmployees.map(emp => (<tr key={emp.userId}><td>{emp.name}</td><td>{emp.department}</td><td>{emp.deductionMultiplier > 1 ? `${emp.deductionMultiplier}x` : '-'}</td></tr>))}</tbody></table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeDashboard;